import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import chatAxiosInstance from "@/lib/chatAxiosInstance";
import { chatEndpoints } from "@/api/chat/chat.endpoints";
import {
  generateDocumentUploadUrls,
  getDocumentItems,
  updateDocuments,
} from "@/api/documents/documents.api";
import { getRegistryFiles } from "@/api/file-registry/fileRegistry.queries";
import { FilesHubQueryKeys } from "@/api/files-hub/filesHub.queries";
import {
  getLinkDownloadUrl,
  getShareDownloadUrl,
  linkApiPath,
  type EntityShareRecord,
} from "@/api/files-hub/fileShares.api";

import type { ChatApiSharedFilesResponse } from "@/api/chat/chat.queries.types";
import type {
  DocumentActionPayload,
  GenerateUploadUrlsPayload,
  GenerateUploadUrlsResponse,
} from "@/api/documents/documents.types";
import type {
  RegistryFilesQuery,
  RegistryPagination,
} from "@/api/file-registry/fileRegistry.types";
import type { FileManagerItem } from "@/components/core/FileManager";

export type FileManagerMount =
  | { type: "work-order"; id: string }
  | { type: "client"; id: string }
  | { type: "entity"; id: string }
  | { type: "chat"; id: string }
  | { type: "user" }
  | { type: "shared"; id: string }
  | {
      type: "shared-with-me";
      id: string;
      readOnly: boolean;
      /** A client or work order opened through an entity share. */
      record?: EntityShareRecord;
    }
  | {
      type: "link";
      token: string;
      /** The link grants Edit: the mount accepts writes through `PUT /files/links/:token/documents`. */
      writable?: boolean;
      record?: EntityShareRecord;
    }
  | { type: "supplier"; id: string }
  | { type: "sp-docs"; id: string }
  | { type: "global"; filters?: RegistryFilesQuery };

export type FileApprovalMode = "folder-copy" | null;

export type RegistryScopedItems = FileManagerItem[] & { registryPagination?: RegistryPagination };

export interface FileManagerMountValue {
  mount: FileManagerMount;
  readOnly: boolean;
  approvalMode: FileApprovalMode;
  allowRootUpload: boolean;
  queryScope: Array<string | null>;
  /**
   * Marks every cached listing that depends on this mount as stale: all folders inside the
   * mount's own file-manager scope, plus the Files-hub browse queries whose cards show a
   * per-resource file count. Mutations must call this, otherwise a folder the user isn't
   * currently looking at (or a hub count) keeps serving a pre-mutation snapshot.
   */
  invalidateScope: () => void;
  browseItems: (parentId: string | null, search: string | null) => Promise<FileManagerItem[]>;
  updateDocuments: (payload: DocumentActionPayload) => Promise<unknown>;
  generateUploadUrls: (
    payload: GenerateUploadUrlsPayload,
  ) => Promise<GenerateUploadUrlsResponse["data"]>;
  /** Set when the listing carries no download links and files are fetched one by one. */
  resolveDownloadUrl?: (item: FileManagerItem) => Promise<string | null>;
}

const FileManagerMountContext = createContext<FileManagerMountValue | null>(null);

export function useFileManagerMount(): FileManagerMountValue {
  const ctx = useContext(FileManagerMountContext);
  if (!ctx) throw new Error("useFileManagerMount must be used within FileManagerMountProvider");
  return ctx;
}

export function resolveDocumentsPath(mount: FileManagerMount): string | null {
  switch (mount.type) {
    case "work-order":
      return `/work-order/${mount.id}/documents`;
    case "client":
      return `/client/documents/${mount.id}`;
    case "entity":
      return `/entity/documents/${mount.id}`;
    case "user":
      return "/files/user/documents";
    case "shared":
      return `/files/shared/${mount.id}/documents`;
    case "supplier":
      return `/files/supplier/${mount.id}/documents`;
    case "shared-with-me":
      return mount.record
        ? `/files/shared-with-me/${mount.id}/records/${mount.record.type}/${mount.record.id}/documents`
        : `/files/shared-with-me/${mount.id}/documents`;
    case "link":
      return mount.record
        ? linkApiPath(mount.token, "records", mount.record.type, mount.record.id, "documents")
        : linkApiPath(mount.token, "documents");
    default:
      return null;
  }
}

// The file-registry backend caps `limit` at 100 (zod max) and 400s above it.
const GLOBAL_PAGE_LIMIT = 100;

async function fetchChatFiles(
  conversationId: string,
  search: string | null,
): Promise<FileManagerItem[]> {
  const { data } = await chatAxiosInstance.get<ChatApiSharedFilesResponse>(
    chatEndpoints.sharedFiles(conversationId),
  );
  const mapped: FileManagerItem[] = (data.files ?? []).map((f) => ({
    id: f.id,
    name: f.fileName,
    type: "FILE",
    parentId: null,
    s3Key: f.fileKey,
    contentType: f.mimeType,
    fileSize: f.sizeBytes,
    tags: [],
    childCount: 0,
    createdAt: f.createdAt,
    updatedAt: f.createdAt,
    downloadUrl: f.url,
  }));
  if (!search) return mapped;
  const term = search.toLowerCase();
  return mapped.filter((f) => f.name.toLowerCase().includes(term));
}

function readOnlyOperation(): Promise<never> {
  return Promise.reject(new Error("This file view is read-only."));
}

async function fetchServiceProviderDocs(
  serviceProviderId: string,
  search: string | null,
): Promise<FileManagerItem[]> {
  const { getServiceProviderHubDocuments } = await import("@/api/files-hub/filesHub.queries");
  const data = await getServiceProviderHubDocuments(serviceProviderId);
  const mapped: FileManagerItem[] = data.items.map((d) => ({
    id: d.id,
    name: d.name,
    type: "FILE",
    parentId: null,
    s3Key: null,
    contentType: null,
    fileSize: null,
    tags: [],
    childCount: 0,
    createdAt: d.createdAt,
    updatedAt: d.createdAt,
    downloadUrl: d.downloadUrl,
  }));
  if (!search) return mapped;
  const term = search.toLowerCase();
  return mapped.filter((f) => f.name.toLowerCase().includes(term));
}

type MountDescriptor = Omit<FileManagerMountValue, "invalidateScope">;

function buildMountValue(mount: FileManagerMount): MountDescriptor {
  const documentsPath = resolveDocumentsPath(mount);

  if (documentsPath) {
    return {
      mount,
      readOnly:
        (mount.type === "link" && !mount.writable) ||
        (mount.type === "shared-with-me" && mount.readOnly),
      approvalMode: mount.type === "work-order" ? "folder-copy" : null,
      allowRootUpload: true,
      queryScope: [
        mount.type,
        "id" in mount ? mount.id : mount.type === "link" ? mount.token : null,
        ...((mount.type === "shared-with-me" || mount.type === "link") && mount.record
          ? [mount.record.id]
          : []),
      ],
      browseItems: (parentId, search) => getDocumentItems(documentsPath, parentId, search),
      updateDocuments: (payload) => updateDocuments(documentsPath, payload),
      generateUploadUrls: (payload) => generateDocumentUploadUrls(documentsPath, payload),
      ...(mount.type === "shared-with-me" && {
        resolveDownloadUrl: (item: FileManagerItem) =>
          getShareDownloadUrl(mount.id, item.id, mount.record),
      }),
      ...(mount.type === "link" && {
        resolveDownloadUrl: (item: FileManagerItem) =>
          getLinkDownloadUrl(mount.token, item.id, mount.record),
      }),
    };
  }

  if (mount.type === "chat") {
    return {
      mount,
      readOnly: true,
      approvalMode: null,
      allowRootUpload: false,
      queryScope: [mount.type, mount.id],
      browseItems: (_parentId, search) => fetchChatFiles(mount.id, search),
      updateDocuments: readOnlyOperation,
      generateUploadUrls: readOnlyOperation,
    };
  }

  if (mount.type === "sp-docs") {
    return {
      mount,
      readOnly: true,
      approvalMode: null,
      allowRootUpload: false,
      queryScope: [mount.type, mount.id],
      browseItems: (_parentId, search) => fetchServiceProviderDocs(mount.id, search),
      updateDocuments: readOnlyOperation,
      generateUploadUrls: readOnlyOperation,
    };
  }

  const filters = mount.type === "global" ? mount.filters : undefined;
  return {
    mount,
    readOnly: true,
    approvalMode: null,
    allowRootUpload: false,
    queryScope: ["global", JSON.stringify(filters ?? {})],
    browseItems: async (_parentId, search) => {
      const data = await getRegistryFiles({
        limit: GLOBAL_PAGE_LIMIT,
        search: search ?? undefined,
        ...filters,
      });
      const items = data.items.map((item) => ({ ...item, parentId: null }));
      // Attaching pagination to the resolved items makes it part of the TanStack Query cache
      // entry itself, so it stays correct on a cache hit -- unlike a queryFn side-effect
      // callback, which only fires when the query actually runs.
      return Object.assign(items, { registryPagination: data.pagination });
    },
    updateDocuments: readOnlyOperation,
    generateUploadUrls: readOnlyOperation,
  };
}

interface FileManagerMountProviderProps {
  mount: FileManagerMount;
  children: ReactNode;
}

export function FileManagerMountProvider({ mount, children }: FileManagerMountProviderProps) {
  const mountId = "id" in mount ? mount.id : null;
  const globalFiltersKey = mount.type === "global" ? JSON.stringify(mount.filters ?? {}) : null;
  const sharedWithMeKey =
    mount.type === "shared-with-me" ? `${mount.readOnly}:${mount.record?.id ?? ""}` : null;
  const linkKey =
    mount.type === "link"
      ? `${mount.token}:${mount.record?.id ?? ""}:${mount.writable ?? false}`
      : null;
  const mountRef = useRef(mount);
  mountRef.current = mount;

  const queryClient = useQueryClient();

  const value = useMemo<FileManagerMountValue>(() => {
    const descriptor = buildMountValue(mountRef.current);
    return {
      ...descriptor,
      invalidateScope: () => {
        void queryClient.invalidateQueries({
          queryKey: ["fileManager", ...descriptor.queryScope],
        });
        // Partial key match: the hub's browse queries key on [browse, kind, page, search], so
        // the bare prefix covers every card list (work orders, clients, service providers,
        // suppliers) whose file counts the mutation may have just changed.
        void queryClient.invalidateQueries({ queryKey: [FilesHubQueryKeys.browse] });
      },
    };
  }, [mount.type, mountId, globalFiltersKey, sharedWithMeKey, linkKey, queryClient]);

  return (
    <FileManagerMountContext.Provider value={value}>{children}</FileManagerMountContext.Provider>
  );
}
